import { APP_NAME } from '@/lib/brand';
/**
 * Fuente única de verdad para la Landing Page.
 *
 * Cuando agregues una nueva vertical, feature, segmento o caso de éxito,
 * solo modifica este archivo — la UI lo consume automáticamente.
 */

// ─── Verticales / Licencias soportadas ─────────────────────────────────
// Cada vertical mapea a un `plan_filter` (el `type` que enviamos a /api/plans/public)
// y a una etiqueta visible para los chips/filtros.
export const VERTICALS = [
  {
    key: 'car_dealer',
    label: 'Automotriz',
    plan_filter: 'car_dealer',
    icon: 'Car',
    desc: 'Car dealers y multi-marca con cotizador financiero completo.',
    pitch: 'Cotización financiera con amortización + seguros, entregable por WhatsApp en minutos.',
  },
  {
    key: 'agency',
    label: 'Inmobiliario',
    plan_filter: 'agency',
    icon: 'Building2',
    desc: 'Agencias, brokers, asesores y franquicias inmobiliarias.',
    pitch: 'Equipos coordinados, disciplina de pipeline y más cierres por vendedor.',
  },
  {
    key: 'developer',
    label: 'Desarrolladores',
    plan_filter: 'developer',
    icon: 'Layers',
    desc: 'Control de inventario, apartado/liberación y cotización con Mapa 2D.',
    pitch: 'Menos fuga de inventario y trazabilidad total por unidad.',
  },
  {
    key: 'education',
    label: 'Educación',
    plan_filter: 'education',
    icon: 'GraduationCap',
    desc: 'Escuelas, universidades y centros de capacitación.',
    pitch: 'Pipeline de inscripciones por ciclo con nurturing automático y recordatorios de pago.',
  },
  {
    key: 'independent',
    label: 'Independientes',
    plan_filter: 'independent',
    icon: 'User',
    desc: 'Asesores independientes que quieren operar con IA sin stacks pesados.',
    pitch: 'Operación ligera con WhatsApp + IA 24/7 y captura automática de leads.',
  },
  {
    key: 'all',
    label: 'Todos los negocios',
    plan_filter: null, // null = no filtra (devuelve todos)
    icon: 'Store',
    desc: 'Comercio, servicios, salud, turismo y cualquier giro que venda hablando con gente.',
    pitch: 'Si atiendes clientes por WhatsApp, redes o llamada, LeadCEM ordena la conversación y el seguimiento hasta el cierre.',
  },
];

// ─── Cómo funciona en 3 pasos ──────────────────────────────────────────
// Secuencia real de arranque (por eso va numerada). Íconos deben existir en ICON_MAP de LandingPage.
export const STEPS = [
  {
    n: '1',
    icon: 'MessageCircle',
    title: 'Conecta tus canales',
    desc: 'WhatsApp oficial, tus anuncios de Meta y Google, tu landing y tu teléfono. En minutos, no en semanas.',
  },
  {
    n: '2',
    icon: 'Bot',
    title: 'La IA contesta y califica',
    desc: 'Responde cada lead en segundos, 24/7. Pregunta lo que importa, manda fichas y precios, y separa al que sí va a comprar.',
  },
  {
    n: '3',
    icon: 'CalendarCheck',
    title: 'Agenda y da seguimiento',
    desc: 'Agenda la cita, asigna al vendedor y persigue al lead hasta que cierra. Tú lo ves todo desde el Módulo de Dirección.',
  },
];

// ─── 30 giros + los módulos REALES que se "encienden" para cada uno ─────
// Los keys de `modules` existen en MODULES (abajo) / plan_access. El diagrama los dibuja y enciende.
// CORE = motor universal que aplica a cualquier negocio; cada giro agrega los suyos.
const CORE = ['ai_agent', 'waba', 'leads', 'agenda', 'opportunities', 'metrics'];

export const GIROS = [
  // Especialidades de LeadCEM (featured)
  { key: 'inmobiliaria', label: 'Inmobiliaria', emoji: '🏠', featured: true,
    pitch: 'Publica, califica y agenda visitas sola. La IA muestra la propiedad correcta y coordina al asesor.',
    modules: [...CORE, 'properties', 'file_library', 'landing', 'capi', 'roulette'] },
  { key: 'autos', label: 'Autos / Agencia', emoji: '🚗', featured: true,
    pitch: 'Cotización financiera con amortización y seguros, entregable por WhatsApp en minutos.',
    modules: [...CORE, 'car_dealer', 'car_builder', 'brands', 'file_library', 'capi'] },
  { key: 'desarrollos', label: 'Desarrollos / preventa', emoji: '🏗️', featured: true,
    pitch: 'Vende sobre plano con Mapa 2D: apartado y liberación por unidad, sin fuga de inventario.',
    modules: [...CORE, 'developments', 'properties', 'file_library', 'roulette'] },
  { key: 'educacion', label: 'Educación / escuelas', emoji: '🎓', featured: true,
    pitch: 'Pipeline de inscripciones por ciclo, con nurturing automático y recordatorios de pago.',
    modules: [...CORE, 'products', 'mass_campaigns', 'marketing', 'comm_logics', 'academy'] },
  { key: 'independiente', label: 'Asesor independiente', emoji: '🧑‍💼', featured: true,
    pitch: 'Opera con IA 24/7 sin herramientas pesadas: captura, contesta y da seguimiento tú solo.',
    modules: [...CORE, 'products', 'vcard', 'comm_logics'] },

  // Cualquier negocio (24 más)
  { key: 'restaurante', label: 'Restaurante', emoji: '🍽️',
    pitch: 'Reservas, pedidos y promos por WhatsApp — y despierta a tu base dormida.',
    modules: [...CORE, 'products', 'mass_campaigns', 'marketing', 'recepcion_ia'] },
  { key: 'gimnasio', label: 'Gimnasio / fitness', emoji: '🏋️',
    pitch: 'Llena clases y membresías: la IA contesta, agenda prueba y reengancha al que se enfría.',
    modules: [...CORE, 'products', 'mass_campaigns', 'comm_logics', 'cem_ratings'] },
  { key: 'clinica', label: 'Clínica / consultorio', emoji: '🩺',
    pitch: 'Agenda citas y contesta llamadas con IA. Recordatorios que bajan el no-show.',
    modules: [...CORE, 'recepcion_ia', 'voice_ai_inbound', 'comm_logics', 'tasks'] },
  { key: 'dentista', label: 'Dentista', emoji: '🦷',
    pitch: 'Cada paciente contestado al instante y reagendado solo. Menos huecos en la agenda.',
    modules: [...CORE, 'recepcion_ia', 'comm_logics', 'mass_campaigns'] },
  { key: 'estetica', label: 'Estética / spa', emoji: '💆',
    pitch: 'Reserva tratamientos, muestra tus paquetes y reactiva clientas con campañas.',
    modules: [...CORE, 'products', 'mass_campaigns', 'comm_logics'] },
  { key: 'hotel', label: 'Hotel / hospedaje', emoji: '🏨',
    pitch: 'Responde disponibilidad y reserva 24/7 por chat y voz, en tu propia marca.',
    modules: [...CORE, 'products', 'recepcion_ia', 'voice_ai_inbound', 'marketing'] },
  { key: 'viajes', label: 'Agencia de viajes', emoji: '✈️',
    pitch: 'Arma paquetes, cotiza y da seguimiento hasta que el cliente reserva.',
    modules: [...CORE, 'products', 'comm_logics', 'marketing', 'mass_campaigns'] },
  { key: 'seguros', label: 'Seguros', emoji: '🛡️',
    pitch: 'Cotiza, recuerda renovaciones y reparte cada prospecto al agente correcto.',
    modules: [...CORE, 'comm_logics', 'tasks', 'commissions', 'marketing'] },
  { key: 'financiera', label: 'Créditos / financiera', emoji: '💳',
    pitch: 'Precalifica por chat, da seguimiento y no dejes enfriar ninguna solicitud.',
    modules: [...CORE, 'comm_logics', 'tasks', 'capi', 'landing'] },
  { key: 'legal', label: 'Despacho legal', emoji: '⚖️',
    pitch: 'Filtra consultas, agenda y da seguimiento a cada caso con tareas claras.',
    modules: [...CORE, 'tasks', 'comm_logics', 'vcard'] },
  { key: 'contable', label: 'Contable / fiscal', emoji: '🧾',
    pitch: 'Atiende, agenda y da seguimiento a clientes con recordatorios automáticos.',
    modules: [...CORE, 'tasks', 'comm_logics', 'marketing'] },
  { key: 'ecommerce', label: 'E-commerce', emoji: '🛒',
    pitch: 'Recupera carritos, resuelve dudas y vende por WhatsApp conectado a Meta.',
    modules: [...CORE, 'products', 'capi', 'mass_campaigns', 'marketing', 'api_hub'] },
  { key: 'retail', label: 'Retail / tienda', emoji: '🏪',
    pitch: 'Atiende cada sucursal, muestra catálogo y reactiva clientes por WhatsApp.',
    modules: [...CORE, 'products', 'mass_campaigns', 'brands', 'marketing'] },
  { key: 'muebles', label: 'Muebles y hogar', emoji: '🛋️',
    pitch: 'Muestra catálogo con foto y precio, cotiza y agenda entrega o visita.',
    modules: [...CORE, 'products', 'file_library', 'mass_campaigns', 'capi'] },
  { key: 'construccion', label: 'Construcción / remodelación', emoji: '🔨',
    pitch: 'Filtra proyectos serios, manda tu portafolio y da seguimiento con tareas.',
    modules: [...CORE, 'products', 'file_library', 'comm_logics', 'tasks'] },
  { key: 'eventos', label: 'Eventos y banquetes', emoji: '🎉',
    pitch: 'Cotiza paquetes, aparta fechas y da seguimiento hasta cerrar el evento.',
    modules: [...CORE, 'products', 'comm_logics', 'mass_campaigns'] },
  { key: 'marketing_ag', label: 'Agencia de marketing', emoji: '📣',
    pitch: 'Captura, contesta y demuestra ROI: cada lead del anuncio cae directo al CRM.',
    modules: [...CORE, 'landing', 'capi', 'ai_independent_campaigns', 'mass_campaigns', 'api_hub'] },
  { key: 'software', label: 'Software / SaaS', emoji: '💻',
    pitch: 'Convierte visitas en demos: la IA califica y agenda con tu equipo comercial.',
    modules: [...CORE, 'landing', 'capi', 'comm_logics', 'api_hub'] },
  { key: 'logistica', label: 'Logística / transporte', emoji: '🚚',
    pitch: 'Atiende cotizaciones y solicitudes 24/7 por chat y voz, con seguimiento.',
    modules: [...CORE, 'recepcion_ia', 'comm_logics', 'tasks', 'api_hub'] },
  { key: 'nutricion', label: 'Nutrición / wellness', emoji: '🥗',
    pitch: 'Agenda consultas, vende planes y reengancha con seguimiento automático.',
    modules: [...CORE, 'products', 'comm_logics', 'mass_campaigns'] },
  { key: 'veterinaria', label: 'Veterinaria', emoji: '🐾',
    pitch: 'Contesta, agenda y recuerda vacunas — cada dueño atendido al instante.',
    modules: [...CORE, 'recepcion_ia', 'comm_logics', 'products'] },
  { key: 'idiomas', label: 'Escuela de idiomas', emoji: '🗣️',
    pitch: 'Inscribe alumnos por ciclo, con campañas y recordatorios que llenan grupos.',
    modules: [...CORE, 'products', 'mass_campaigns', 'comm_logics', 'academy'] },
  { key: 'autopartes', label: 'Refacciones / autopartes', emoji: '🔧',
    pitch: 'Cotiza piezas por chat con catálogo multimarca, y da seguimiento.',
    modules: [...CORE, 'products', 'brands', 'capi', 'mass_campaigns'] },
  { key: 'renta', label: 'Bienes raíces en renta', emoji: '🔑',
    pitch: 'Filtra interesados, muestra la unidad y agenda visita — sin perseguir a nadie.',
    modules: [...CORE, 'properties', 'file_library', 'comm_logics', 'roulette'] },
  { key: 'franquicia', label: 'Franquicia / multi-sucursal', emoji: '🏬',
    pitch: 'Controla todas tus sucursales en un tablero, con reportes de dirección por unidad.',
    modules: [...CORE, 'brands', 'roulette', 'direccion', 'mgmt_reports', 'team'] },
];

// ─── Features destacadas del producto ──────────────────────────────────
// Reemplaza al array FEATURES estático y se renderiza dinámicamente.
export const FEATURES = [
  {
    icon: 'Inbox',
    title: 'Social Inbox conectado a Meta',
    desc: 'Atiende Facebook, Instagram DM, Messenger y WhatsApp desde una sola bandeja. Conexión real vía Meta Graph API.',
    accent: 'cyan',
  },
  {
    icon: 'Mail',
    title: 'Marketing con Mailjet + drag & drop',
    desc: 'Diseña emails con editor visual, lanza secuencias automáticas y mide aperturas/clicks en tiempo real.',
    accent: 'violet',
  },
  {
    icon: 'Bot',
    title: 'IA que califica prospectos 24/7',
    desc: 'Prioriza por intención, resume contexto del lead y dispara acciones comerciales sin intervención humana.',
    accent: 'emerald',
  },
  {
    icon: 'Wand2',
    title: 'Campañas IA Independientes',
    desc: 'La IA configura, ejecuta y optimiza campañas multicanal por su cuenta. Tú solo defines el objetivo.',
    accent: 'amber',
  },
  {
    icon: 'Layers',
    title: 'Cotizador Mapa 2D para desarrollos',
    desc: 'Apartado/liberación visual sobre el plano del proyecto. Auditabilidad total por unidad y comisión.',
    accent: 'sky',
  },
  {
    icon: 'Car',
    title: 'Cotizador Financiero Automotriz',
    desc: 'Amortización, seguros, planes de financiamiento y entregables al cliente por WhatsApp/Email en minutos.',
    accent: 'rose',
  },
  {
    icon: 'GitBranch',
    title: 'Pipeline inteligente',
    desc: 'Etapas configurables con probabilidad ponderada, forecast automático y alertas de pipeline en riesgo.',
    accent: 'teal',
  },
  {
    icon: 'UserPlus',
    title: 'Lead Creation simplificado',
    desc: 'Crea leads en 3 clicks desde cualquier canal. La IA los enriquece y los enruta automáticamente.',
    accent: 'indigo',
  },
  {
    icon: 'Users',
    title: 'Ruleta y asignación inteligente',
    desc: 'Distribución por disponibilidad, zona, especialidad, grupo y reglas de operación. Sin colas muertas.',
    accent: 'fuchsia',
  },
];

// ─── Galería de módulos: categorías (tabs) ─────────────────────────────
// accent → clave de ACCENT_BG (color del chip de ícono por categoría, no por módulo).
export const MODULE_CATEGORIES = [
  { key: 'capta',       label: 'Capta y contesta',  accent: 'lime' },
  { key: 'vende',       label: 'Vende',             accent: 'navy' },
  { key: 'seguimiento', label: 'Da seguimiento',    accent: 'cyan' },
  { key: 'direccion',   label: 'Dirige y controla', accent: 'slate' },
];

// ─── Galería de +30 módulos con contexto ───────────────────────────────
// key alineado a plan_access (MENU_MODULES / PLAN_CAPABILITIES); plan_key = key
// real del entitlement cuando el nombre comercial difiere; capability = true si es
// PLAN_CAPABILITIES; only_leadcem = badge diferenciador. La galería es PRESENTACIÓN:
// listar un módulo NO lo concede — el plan/override manda en runtime.
export const MODULES = [
  // ── Capta y contesta ──────────────────────────────────────────────
  { key: 'ai_agent',          icon: 'Bot',            name: 'Vendedor IA en WhatsApp',  category: 'capta', context: 'Contesta cada lead en segundos, 24/7 — mientras duermes, él vende y agenda la cita.' },
  { key: 'voice_ai_inbound',  icon: 'PhoneIncoming',  name: 'Voz IA Entrante',          category: 'capta', capability: true, context: 'Cuando te llaman, la IA contesta, informa y agenda. Ninguna llamada al vacío.' },
  { key: 'voice_ai_outbound', icon: 'PhoneOutgoing',  name: 'Voz IA Saliente',          category: 'capta', capability: true, context: 'La IA llama al lead que dejó su número y lo reengancha antes de que se enfríe.' },
  { key: 'voice_realtime',    icon: 'AudioLines',     name: 'Voz IA en Tiempo Real',    category: 'capta', capability: true, context: 'Conversación de voz natural, sin robotadas ni pausas: el lead no nota la diferencia.' },
  { key: 'recepcion_ia',      icon: 'PhoneCall',      name: 'Recepción IA',             category: 'capta', capability: true, context: 'Número de rastreo por campaña, enrutado por horario. Sabes de qué anuncio vino cada llamada.' },
  { key: 'callforce',         icon: 'Radar',          name: 'Call Force IA',            category: 'capta', capability: true, only_leadcem: true, context: 'Analiza cada llamada (IA vs humano) y te dice quién cierra y quién deja ir el dinero.' },
  { key: 'waba',              icon: 'MessageCircle',  name: 'WhatsApp Oficial (WABA)',  category: 'capta', context: 'Tu marca con palomita verde, en tu número. Verificado, sin bloqueos ni número personal quemado.' },
  { key: 'landing',           icon: 'LayoutTemplate', name: 'Páginas que Capturan',     category: 'capta', context: 'El clic del anuncio se vuelve conversación: la landing cae directo al Agente IA.' },
  { key: 'capi',              icon: 'Facebook',       name: 'Conexión con Meta (CAPI)', category: 'capta', context: 'Meta aprende quién te compra de verdad y te trae más — menos dinero tirado por lead.' },
  { key: 'api_hub',           icon: 'Webhook',        name: 'Conecta Todo (API Hub)',   category: 'capta', context: 'Enlaza tus formularios, portales y herramientas. Todo tu negocio en un solo flujo.' },
  // ── Vende ─────────────────────────────────────────────────────────
  { key: 'products',      icon: 'Package',       name: 'Catálogo de Productos',     category: 'vende', context: 'La IA conoce tu catálogo y lo vende con foto, precio y ficha al instante.' },
  { key: 'properties',    icon: 'Home',          name: 'Inventario de Propiedades', category: 'vende', context: 'Cada propiedad lista para que la IA la muestre y agende la visita.' },
  { key: 'developments',  icon: 'Map',           name: 'Desarrollos en Preventa',   category: 'vende', context: 'Vende sobre plano: Mapa 2D con apartado/liberación por unidad, sin fuga de inventario.' },
  { key: 'car_dealer',    icon: 'Car',           name: 'Piso de Autos',             category: 'vende', context: 'Cotización financiera con amortización y seguros, entregable por WhatsApp en minutos.' },
  { key: 'car_builder',   icon: 'Wrench',        name: 'Armador de Autos',          category: 'vende', context: 'El cliente arma versión y color; tú recibes el interesado listo para cerrar.' },
  { key: 'brands',        icon: 'Tags',          name: 'Multi-marca',               category: 'vende', context: 'Varias marcas o sucursales en un tablero, sin revolver inventarios ni equipos.' },
  { key: 'file_library',  icon: 'Library',       name: 'Biblioteca de Materiales',  category: 'vende', context: 'Fotos, videos y fichas a la mano: la IA manda el material correcto en la conversación.' },
  { key: 'agenda',        icon: 'CalendarCheck', name: 'Agenda de Citas',           category: 'vende', context: 'La IA agenda citas reales y las recuerda. Menos no-shows, más visitas.' },
  { key: 'roulette',      icon: 'Shuffle',       name: 'Ruleta de Asignación',      category: 'vende', plan_key: 'rules', context: 'Reparte cada lead al vendedor correcto por zona y turno — cero leads muertos en cola.' },
  // ── Da seguimiento ────────────────────────────────────────────────
  { key: 'ai_independent_campaigns', icon: 'Wand2',      name: 'Campañas IA',             category: 'seguimiento', context: 'La IA arma, lanza y optimiza campañas por producto. Tú solo defines el objetivo.' },
  { key: 'mass_campaigns',           icon: 'Megaphone',  name: 'Campañas Masivas',        category: 'seguimiento', context: 'Despierta a tu base dormida por WhatsApp con segmentos y plantillas aprobadas.' },
  { key: 'voice_blaster',            icon: 'RadioTower', name: 'Voice Blaster',           category: 'seguimiento', plan_key: 'mass_campaigns', context: 'Llamadas de voz IA masivas para reenganchar cientos de leads a la vez.' },
  { key: 'comm_logics',              icon: 'Waypoints',  name: 'Lógicas de Comunicación', category: 'seguimiento', context: 'Secuencias WhatsApp + voz + correo que siguen solas hasta la respuesta.' },
  { key: 'marketing',                icon: 'Mail',       name: 'Correos y Mailings',      category: 'seguimiento', context: 'Correos que venden a tu base, con editor visual y secuencias — sin otra herramienta aparte.' },
  // ── Dirige y controla ─────────────────────────────────────────────
  { key: 'leads',         icon: 'Contact',         name: 'Bandeja de Leads',        category: 'direccion', context: 'Todos tus leads en un lugar, enriquecidos y enrutados. Nada se pierde en un chat.' },
  { key: 'opportunities', icon: 'Columns3',        name: 'Embudo de Ventas',        category: 'direccion', context: 'Ve en qué paso está cada venta y qué falta para cerrarla, con alertas de pipeline en riesgo.' },
  { key: 'tasks',         icon: 'ListTodo',        name: 'Administrador de Tareas', category: 'direccion', plan_key: 'agenda', context: 'Cada vendedor sabe a quién llamar hoy — se acabó el "se me pasó".' },
  { key: 'metrics',       icon: 'BarChart3',       name: 'Métricas en Vivo',        category: 'direccion', context: 'Velocidad de respuesta, conversión y actividad por vendedor — deja de adivinar con tu dinero.' },
  { key: 'cem_ratings',   icon: 'TrendingUp',      name: 'Pronóstico de Ventas',    category: 'direccion', context: 'Calificación de leads y pronóstico de cierre para priorizar lo caliente.' },
  { key: 'commissions',   icon: 'BadgeDollarSign', name: 'Comisiones',              category: 'direccion', context: 'Cada vendedor ve lo que gana — y vende más por verlo. Transparente y auditable.' },
  { key: 'direccion',     icon: 'Compass',         name: 'Módulo de Dirección',     category: 'direccion', only_leadcem: true, context: 'El tablero del dueño: el pulso del negocio en una vista. Diriges con datos, no con corazonadas.' },
  { key: 'mgmt_reports',  icon: 'FileBarChart',    name: 'Reportes Gerenciales',    category: 'direccion', only_leadcem: true, context: 'Reportes ejecutivos listos para junta: por equipo, campaña y vertical. Descárgalos y preséntalos.' },
  { key: 'team',          icon: 'Users',           name: 'Equipo y Roles',          category: 'direccion', context: 'Cada quien ve lo suyo — tu información no se va con quien renuncia.' },
  { key: 'vcard',         icon: 'CreditCard',      name: 'Tarjeta Digital',         category: 'direccion', context: 'Tu vendedor comparte su tarjeta y el lead cae directo al sistema.' },
  { key: 'academy',       icon: 'GraduationCap',   name: 'Academia',                category: 'direccion', context: 'Capacita al que entra sin que tú pares a enseñarle.' },
  { key: 'dashboard',     icon: 'LayoutDashboard', name: 'Tablero Principal',       category: 'direccion', context: 'Abres y de un vistazo sabes cómo va tu negocio hoy: leads, citas y pendientes.' },
];

// ─── Bloque "Solo LeadCEM" (diferenciación — todo real, ver plan_access) ─
export const SOLO_LEADCEM = [
  { icon: 'Bot',          title: 'La IA usa herramientas reales, no habla por hablar', desc: 'Agenda citas, manda fichas y media de TU inventario, asigna al vendedor y transfiere la llamada. No es un chatbot con guiones.' },
  { icon: 'Radar',        title: 'Call Force IA: analiza cada llamada',                desc: 'Escucha la llamada de la IA y la de tus vendedores, y te dice —sin favoritismos— quién cierra y quién deja ir el dinero.' },
  { icon: 'Compass',      title: 'Módulo de Dirección para el dueño',                  desc: 'El asiento del piloto: entradas, seguimientos, citas y cierres de todo tu equipo en una vista. Duermes tranquilo.' },
  { icon: 'FileBarChart', title: 'Reportes Gerenciales listos para junta',            desc: 'Llega con los números en la mano, no con excusas — sin que nadie pase la noche armando un Excel.' },
  { icon: 'AudioLines',   title: 'Voz IA en tu propio número',                         desc: 'WhatsApp + llamadas IA en el número y la marca de tu negocio, 24/7. Multicanal de verdad, no un widget.' },
  { icon: 'Package',      title: 'Multi-vertical con TU inventario',                   desc: 'Propiedades, desarrollos, autos o productos: la IA responde con tus datos reales, no con respuestas genéricas.' },
];

// ─── 3 Pilares del producto (hero overview) ────────────────────────────
export const PILLARS = [
  { icon: 'BarChart3', title: 'CRM comercial', desc: 'Leads, oportunidades, cotizaciones y desempeño por equipo bajo control total.' },
  { icon: 'Workflow', title: 'CEM inteligente', desc: 'Seguimiento automatizado con IA, campañas y ejecución comercial 24/7.' },
  { icon: 'Globe', title: 'Ecosistema de exposición', desc: 'Portal público, sitios white-label, fichas y captación conectadas a ventas reales.' },
];

// ─── Métricas de "social proof" ────────────────────────────────────────
// Métricas HONESTAS: verificables o estructurales — sin porcentajes inventados.
// (Si Sergio aporta cifras reales defendibles con dato/fuente, se sustituyen aquí.)
export const PROOF_METRICS = [
  ['Segundos', 'lo que tarda la IA en contestar cada lead, a cualquier hora'],
  ['24/7', 'seguimiento comercial que no descansa, en todos los canales'],
  ['+30', 'módulos en una sola plataforma: CRM, CEM, Voz IA y tareas'],
  ['360°', 'CRM + CEM + exposición + IA, sin herramientas sueltas'],
];

// ─── Casos de uso por industria ────────────────────────────────────────
export const CASES = [
  { vertical: 'Desarrollador', headline: 'Cotizaciones auditables + control de apartados/liberación = menos fuga de inventario.' },
  { vertical: 'Agencia broker', headline: 'Más seguimiento en menos tiempo, con trazabilidad por asesor y etapa.' },
  { vertical: 'Automotriz', headline: 'Cotización financiera completa y entregable por WhatsApp/Email en minutos.' },
  { vertical: 'Educación', headline: 'Pipeline por ciclo escolar, recordatorios automáticos y nurturing de prospectos.' },
  { vertical: 'Retail', headline: 'Captación y nurturing continuos con menos dependencia de procesos manuales.' },
];

// ─── URLs externas reutilizables ───────────────────────────────────────
export const EXTERNAL_URLS = {
  public_portal: 'https://propiedades.dealider.com',
  logo: '/brand/leadcem-logo.png',
  // WhatsApp del tenant casa LeadCEM (Agente IA "Carlo") — para los CTAs (Fase 1)
  whatsapp: 'https://wa.me/5215597088217?text=Hola,%20quiero%20probar%20el%20Agente%20IA%20de%20LeadCEM',
  // CTA de voz (Fase 4): honesto — pide al agente real una llamada de la IA por voz (no inventa número)
  whatsappVoice: 'https://wa.me/5215597088217?text=Hola,%20quiero%20que%20la%20IA%20de%20LeadCEM%20me%20llame%20por%20voz%20para%20probarla',
};

// ─── SEO / JSON-LD (Fase 0) ────────────────────────────────────────────
export const SEO = {
  name: 'LeadCEM',
  url: 'https://app.leadcem.com/',
  logo: 'https://app.leadcem.com/brand/leadcem-logo.png',
  description: 'Plataforma omnicanal con Agente IA que contesta, califica y da seguimiento a tus leads por WhatsApp y voz 24/7, y agenda la cita. CEM + CRM + administrador de tareas.',
  category: 'BusinessApplication',
};

// ─── FAQ (Fase 0: alimenta el FAQPage JSON-LD; Fase 5: la sección visible) ─
export const FAQS = [
  { q: '¿Es un chatbot de esos que no entienden nada?', a: 'No. Un chatbot sigue un guión; la IA de LeadCEM usa herramientas reales: consulta tu inventario, manda fichas y fotos, agenda citas de verdad, asigna al vendedor y hasta transfiere la llamada. Contesta con tus datos, no con respuestas genéricas.' },
  { q: '¿Mis clientes no se van a molestar por hablar con una máquina?', a: 'Lo que molesta no es la máquina: es esperar. La IA contesta en segundos a cualquier hora y, cuando conviene, te pasa el cliente calientito a un humano.' },
  { q: 'No soy muy de tecnología. ¿Yo tengo que configurarlo?', a: 'No. Alguien real se sienta contigo, entiende tu negocio y deja todo funcionando con tu forma de vender, tus productos y tus precios. No lo armas: lo armamos contigo.' },
  { q: '¿Está caro?', a: 'Haz la cuenta al revés: ¿cuánto te costó no contestar a tiempo el mes pasado? LeadCEM cuesta menos que una de las ventas que hoy se te escapan. Hay un plan para cada tamaño de negocio.' },
  { q: '¿Funciona para mi giro?', a: 'Sí. LeadCEM es multi-giro: el mismo motor adaptado a inmobiliaria, autos, desarrollos, educación, salud, servicios y comercio. Si vendes hablando con gente, es para ti.' },
  { q: '¿Usa mi número? ¿Es WhatsApp oficial?', a: 'Sí. Trabaja con WhatsApp Oficial (API) verificado, en el número y la marca de tu negocio, con palomita verde y sin quemar tu número personal. Y la Voz IA llama desde tu propio número.' },
];
